import type { LandmarkDef } from './common';
import { landmarkModel } from './model';

/**
 * landmarks/tokyo.ts — Tokyo's neon-night landmarks. Kaminarimon + torii are
 * drive-through gateways ('span') scaled by width so the road threads cleanly
 * through.
 *
 * GS-Tokyo Blender round (2026-08-08):
 * - tokyoTower is a REAL lattice tower now — a CC-BY Sketchfab model (see
 *   public/models/landmarks/CREDITS.md), processed in Blender: scan-context
 *   terrain stripped, welded 82k→31k verts, UVs dropped (untextured CAD-style
 *   model), metallic-1.0 trap zeroed, materials renamed to classifier-safe
 *   `tt_*` names (red/dark/steel hit the SKIP regex; the rest miss every
 *   role), warm floodlit-orange emissive whisper baked in (the real tower's
 *   night "Landmark Light") + lit observation-deck bands. No skipTexturing
 *   needed — the names carry the bypass (the classifier-miss path).
 * - torii/kaminarimon/pagoda vermilion parts wear a BAKED lacquer material
 *   (`*_lacquer`): procedural albedo + roughness + emissive maps painted in
 *   Blender (vertical grain, glossy lacquer, sparse warm CATCH-LIGHT streaks
 *   in the emissive map — so torii's ×1.7 emissiveScale below modulates
 *   through the texture as lacquer highlights, never whole-material glow).
 * - pagoda stories: 'white' was classifying as plaster → the tan triplanar
 *   read as GRANITE at night; renamed `pgd_shirokabe` (classifier miss) with
 *   a near-white flat albedo. Roofs are real volumes now (thickness, deep
 *   eaves, upturned corners) on `pgd_tile`/`kmn_tile`/`kasagi_tile` — the
 *   /tile/ ROOFTILE triplanar role, tinted dark charcoal.
 * - fuji: the old GLB's profile was INVERTED (widest ring at the top — the
 *   giant navy blob QA flagged); rebuilt as a proper concave volcano with a
 *   moonlit snow cap (whisper emissive + zig-zag snow fingers).
 *
 * GS-Tokyo NIT ROUND (2026-08-10), all three model-side (no def changes):
 * - shibuyaTower read as the ONLY pale untextured mass in a near-black canyon.
 *   ROOT CAUSE was not a light albedo alone: its podium AND cylinder shared
 *   `sb_steel_panel` (base 0.52 grey) which the triplanar classifier rejects
 *   TWICE — metalness 0.55 > 0.5, and 'steel' is in the SKIP regex — so no
 *   role could ever tone or grain it. Rebuilt as dark glazed cladding on
 *   `sb_clad_conc` (the /conc/ CONCRETE role; misses SKIP + PLASTER, metal
 *   0.12 / rough 0.66 so classify() actually reaches the name). The concrete
 *   atlas multiplies diffuse by ~0.43 (mean 0.245 x 1.75), so the authored
 *   base is deliberately ABOVE target at 0.24 — under ~0.15 the shell swings
 *   straight from "pale slab" to invisible. Shaft is a spun stepped profile
 *   (9 retail floors: recessed lit glazing ribbon + proud spandrel) with 24
 *   aluminium mullion fins; podium is stacked bands so the glazed shopfront
 *   is a real opening under an overhanging canopy. MEASURED: the first pass
 *   simply MOVED the pale mass onto the emissive glass — big continuous lit
 *   slabs blow to flat cream — so glazing was narrowed 2.25 -> 1.45 and both
 *   window emissives cut hard. The mega-screen (`sb_screen_sign` + its baked
 *   emissive map) is untouched; new cladding stays inside r 7.5 so it can
 *   never poke through the screen surface at r 7.65.
 * - pagoda: `pgd_shirokabe` was a near-white 0.945 flat (classifier-miss by
 *   design, so nothing tones it) reading chalky and blowing out against the
 *   near-black roofs, with a hard lit/shadow split across each bare box.
 *   Warmed + lowered to 0.70, and the two white stories gained real shinkabe
 *   timber framing (corner + intermediate posts, three nageshi rails) on the
 *   lacquer material — authentic construction that breaks each big flat face
 *   into panels. The framing carries its own tiled UVs so it wears the same
 *   lacquer grain (glTF samplers default to REPEAT).
 * - fuji: flat navy cutout, hard-edged snow cap, hard silhouette at the ground.
 *   Fixed with VERTEX COLOURS (COLOR_0). KEY: COLOR_0 multiplies baseColor so
 *   it can only DARKEN — the `indigo` base was therefore RAISED to the haze
 *   value (0.30/0.32/0.42) and per-vertex darkening carves the gradient
 *   downward, which means the base stays hazy/warm and dissolves into the
 *   horizon instead of meeting the ground on a hard line. Same attribute adds
 *   broad azimuthal gullies, and feathers the snowline in VALUE (finger tips
 *   darken toward the rock) on top of 30 dappled patches below the line.
 *   Export needs `export_vertex_color='ACTIVE'` — the default 'MATERIAL' only
 *   emits COLOR_0 when the material graph reads the attribute.
 */
export const TOKYO_LANDMARKS: Record<string, LandmarkDef> = {
  // GS-Tokyo: height 42 → 56 — the 109-style cylinder + curved corner
  // mega-screen ('sb_screen_sign', self-lit, skips triplanar) was buried
  // behind the Shibuya billboard blocks at 42; at 56 the screen band clears
  // the front-row rooftops. (The forecourt clearing itself is the Tokyo TS
  // round's job — see that round's work order.)
  shibuyaTower: { build: () => landmarkModel('models/landmarks/shibuya_tower.glb', { height: 56 }), placement: 'side', offset: 12 },
  kaminarimon: { build: (hw) => landmarkModel('models/landmarks/kaminarimon.glb', { width: 2 * hw + 18 }), placement: 'span' },
  pagoda: { build: () => landmarkModel('models/landmarks/pagoda.glb', { height: 32 }), placement: 'side', offset: 12 },
  tokyoTower: { build: () => landmarkModel('models/landmarks/tokyo_tower.glb', { height: 78 }), placement: 'side', offset: 22 },
  fuji: { build: () => landmarkModel('models/landmarks/fuji.glb', { height: 80 }), placement: 'side', offset: 200 },
  // M97: the vermilion glows a touch hotter so the finish gateway reads against
  // the neon night instead of flat matte orange. (GS-Tokyo: the 1.7× now rides
  // the baked lacquer emissive MAP — bright catch-lights, not a flat glow.)
  torii: {
    build: (hw) => landmarkModel('models/landmarks/torii.glb', { width: 2 * hw + 18, emissiveScale: 1.7 }),
    placement: 'span',
  },
};
